import { useQuery } from '@tanstack/react-query'
import { CalendarDays, CalendarX, TriangleAlert } from 'lucide-react'

import { businessDayQueryOptions } from '@/api/reference-api'
import { ErrorSurface } from '@/components/error-surface'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { BUSINESS_DAY_STATUS_LABEL, BUSINESS_DAY_STATUS_TONE } from '@/features/ledger/constants'
import { today } from '@/features/reports/constants'
import { formatBusinessDate } from '@/lib/datetime'

import { useOperatorBranchId } from './operator-branch'

/**
 * The first thing a teller needs to know: which day the branch is trading in, and whether that is
 * today at all.
 *
 * Read through `reference-api.ts#businessDayQueryOptions` — the same query and the same cache entry
 * the application header uses, so this tile and the header can never disagree about a branch's
 * business date. It carries no permission requirement on the server, which is why nothing gates it
 * here either: the ledger date is what every screen in the institution stamps its work with, not a
 * fact reserved for the ledger module.
 *
 * A day that is open is not necessarily today's day. A branch that has not yet closed yesterday is
 * still working in yesterday, and a teller who has not been told that will hand over a receipt
 * dated wrong. So this tile does not stop at "open" — it compares the business date against the
 * calendar and says so, plainly, when they differ.
 */
export function BusinessDayTile() {
  const branchId = useOperatorBranchId()
  const query = useQuery(businessDayQueryOptions(branchId))
  const calendarToday = today()

  return (
    <Card className="flex flex-col">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CalendarDays className="size-4 text-muted" aria-hidden />
          Business day
        </CardTitle>
        {query.data ? (
          <Badge tone={BUSINESS_DAY_STATUS_TONE[query.data.status] ?? 'warning'}>
            {BUSINESS_DAY_STATUS_LABEL[query.data.status] ?? query.data.status}
          </Badge>
        ) : null}
      </CardHeader>

      <CardContent className="flex-1 space-y-2">
        {query.isPending ? (
          <Skeleton className="h-14 w-full" />
        ) : query.isError ? (
          <ErrorSurface
            error={query.error}
            onRetry={() => void query.refetch()}
            title="The business day could not be read"
          />
        ) : !query.data || query.data.businessDate === null ? (
          <p className="flex items-start gap-1.5 text-sm text-body">
            <CalendarX className="mt-0.5 size-4 shrink-0 text-warning-text" aria-hidden />
            No business day is open at this branch. Nothing will post until one is.
          </p>
        ) : (
          <>
            <p className="numeric text-lg font-semibold tabular-nums text-heading">
              {formatBusinessDate(query.data.businessDate)}
            </p>

            {query.data.businessDate !== calendarToday ? (
              <p className="flex items-start gap-1.5 rounded-control border border-warning-border bg-warning-subtle p-2 text-xs text-warning-text">
                <TriangleAlert className="mt-0.5 size-3.5 shrink-0" aria-hidden />
                Not today. The branch is still working {formatBusinessDate(query.data.businessDate)}
                &nbsp;— every posting made now lands on that date, not the calendar's.
              </p>
            ) : null}
          </>
        )}
      </CardContent>
    </Card>
  )
}
